import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useConfigs, useUpdateConfig } from "@finitless/shared/hooks";
import { Icon, Button, NativeSelect } from "../../";

const DriverCompanySettings = ({ pluginKey }) => {
  const [formState, setFormState] = useState({ changes: [] });
  const queryClient = useQueryClient();
  const updateConfigMutation = useUpdateConfig();

  // Build where clause based on plugin key
  const where = pluginKey
    ? JSON.stringify([
        {
          attribute: "key",
          value: { condition: "ilike", value: `%${pluginKey}%` },
          conector: "AND",
        },
      ])
    : undefined;

  const {
    data: configs = [],
    isLoading,
    error,
  } = useConfigs({ where }, { enabled: !!pluginKey });

  // Filter out the feature flags (configs without config_category_id)
  const driverConfigs = configs.filter((config) => config.config_category_id);

  const handleInputChange = (value, configId) => {
    const config = driverConfigs.find((c) => c.id === configId);
    if (!config) return;

    setFormState((prev) => {
      const existingIndex = prev.changes.findIndex((c) => c.id === configId);
      const newChange = {
        id: configId,
        key: config.key,
        name: config.name,
        value,
      };

      if (existingIndex >= 0) {
        const newChanges = [...prev.changes];
        newChanges[existingIndex] = newChange;
        return { changes: newChanges };
      } else {
        return { changes: [...prev.changes, newChange] };
      }
    });
  };

  const handleSelectChange = (value, configId) => {
    handleInputChange(value, configId);
  };

  const handleCheckBoxChange = (e, isString, currentValue) => {
    const { name, checked, dataset } = e.target;
    const configId = parseInt(dataset.id);

    let newValue;
    if (isString) {
      const values = currentValue.split("|").filter((v) => v);
      if (checked) {
        values.push(name);
      } else {
        const index = values.indexOf(name);
        if (index > -1) values.splice(index, 1);
      }
      newValue = values.join("|");
    } else {
      const values = JSON.parse(currentValue || "[]");
      const numValue = parseInt(name);
      if (checked) {
        values.push(numValue);
      } else {
        const index = values.indexOf(numValue);
        if (index > -1) values.splice(index, 1);
      }
      newValue = JSON.stringify(values);
    }

    handleInputChange(newValue, configId);
  };

  const handleSubmit = async () => {
    try {
      for (const change of formState.changes) {
        await updateConfigMutation.mutateAsync({
          configId: change.id,
          value: change.value,
        });
      }
      // Wait for React Query to refetch before clearing local changes
      await queryClient.invalidateQueries({ queryKey: ["configs"] });
      setFormState({ changes: [] });
    } catch (error) {
      console.error("Error updating configs:", error);
    }
  };

  const handleKeyPress = (e, key) => {
    if (key.includes("fee") || key.includes("cost")) {
      if (!/^[0-9.]$/.test(e.key)) {
        e.preventDefault();
      }
    }
  };

  if (!pluginKey) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="border-border bg-background rounded-lg border p-6">
        <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
          <Icon
            name="Settings"
            className="text-muted-foreground mr-2 h-5 w-5"
          />
          Configuration Settings
        </h3>
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i}>
              <div className="bg-card mb-2 h-4 w-32 rounded"></div>
              <div className="bg-card h-10 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-border bg-background rounded-lg border p-6">
        <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
          <Icon
            name="Settings"
            className="text-muted-foreground mr-2 h-5 w-5"
          />
          Configuration Settings
        </h3>
        <div className="text-destructive text-sm">
          Error loading configurations: {error.message}
        </div>
      </div>
    );
  }

  if (driverConfigs.length === 0) {
    return (
      <div className="border-border bg-background rounded-lg border p-6">
        <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
          <Icon
            name="Settings"
            className="text-muted-foreground mr-2 h-5 w-5"
          />
          Configuration Settings
        </h3>
        <div className="text-muted-foreground text-sm">
          No configuration settings available for this service.
        </div>
      </div>
    );
  }

  return (
    <div className="border-border bg-background rounded-lg border p-6">
      <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
        <Icon name="Settings" className="text-muted-foreground mr-2 h-5 w-5" />
        Configuration Settings
      </h3>

      <div className="space-y-4">
        {driverConfigs.map((config) => (
          <div key={config.id}>
            {/* Type 1: Text Input / Type 7: Password Input */}
            {(config.type === 1 || config.type === 7) && (
              <div className="space-y-2">
                <label
                  htmlFor={`driver-config-${config.id}`}
                  className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium"
                >
                  {config.name}
                </label>
                {config.description && (
                  <p className="text-muted-foreground mb-2 text-xs">
                    {config.description}
                  </p>
                )}
                <input
                  type={config.type === 7 ? "password" : "text"}
                  value={
                    formState.changes.find((c) => c.id === config.id)?.value ??
                    config.value ??
                    ""
                  }
                  onChange={(e) => handleInputChange(e.target.value, config.id)}
                  onKeyDown={(e) => handleKeyPress(e, config.key)}
                  className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none"
                  placeholder={config.name}
                />
              </div>
            )}

            {/* Type 2: Select/Dropdown */}
            {config.type === 2 && config.options && (
              <div className="space-y-2">
                <label className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium">
                  {config.name}
                </label>
                {config.description && (
                  <p className="text-muted-foreground mb-2 text-xs">
                    {config.description}
                  </p>
                )}
                <NativeSelect
                  id={`driver-config-${config.id}`}
                  value={
                    formState.changes.find((c) => c.id === config.id)?.value ??
                    config.value ??
                    ""
                  }
                  onChange={(e) =>
                    handleSelectChange(e.target.value, config.id)
                  }
                  className="custom-select border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:outline-none"
                >
                  {config.options.map((option, idx) => (
                    <option key={idx} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}

            {/* Type 3: Multiple Checkboxes (string separated by |) */}
            {config.type === 3 && config.options && (
              <div className="space-y-2">
                <label className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium">
                  {config.name}
                </label>
                {config.description && (
                  <p className="text-muted-foreground mb-2 text-xs">
                    {config.description}
                  </p>
                )}
                <div className="space-y-2">
                  {config.options.map((option, idx) => {
                    const currentValue =
                      formState.changes.find((c) => c.id === config.id)
                        ?.value ??
                      config.value ??
                      "";
                    const isChecked = currentValue
                      .split("|")
                      .includes(option.value);

                    return (
                      <label
                        key={idx}
                        className="text-foreground flex cursor-pointer items-center space-x-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          name={option.value}
                          data-id={config.id}
                          checked={isChecked}
                          onChange={(e) =>
                            handleCheckBoxChange(e, true, currentValue)
                          }
                          className="border-border bg-card text-primary focus:ring-ring h-4 w-4 rounded focus:ring-2"
                        />
                        <span>{option.text}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Type 4: Multiple Checkboxes (JSON array) or Text (for driver_tip_options) */}
            {config.type === 4 && (
              <>
                {config.key === "driver_tip_options" ? (
                  <div className="space-y-2">
                    <label className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium">
                      {config.name}
                    </label>
                    {config.description && (
                      <p className="text-muted-foreground mb-2 text-xs">
                        {config.description}
                      </p>
                    )}
                    <input
                      type="text"
                      value={
                        formState.changes.find((c) => c.id === config.id)
                          ?.value ??
                        // eslint-disable-next-line no-useless-escape
                        config.value?.replace(/[\[\]]/g, "") ??
                        ""
                      }
                      onChange={(e) =>
                        handleInputChange(e.target.value, config.id)
                      }
                      className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none"
                      placeholder="e.g., 5,10,15"
                    />
                  </div>
                ) : (
                  config.options && (
                    <div className="space-y-2">
                      <label className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium">
                        {config.name}
                      </label>
                      {config.description && (
                        <p className="text-muted-foreground mb-2 text-xs">
                          {config.description}
                        </p>
                      )}
                      <div className="space-y-2">
                        {config.options.map((option, idx) => {
                          const currentValue =
                            formState.changes.find((c) => c.id === config.id)
                              ?.value ??
                            config.value ??
                            "[]";
                          const parsedValues = JSON.parse(currentValue);
                          const isChecked = parsedValues.includes(
                            parseInt(option.value),
                          );

                          return (
                            <label
                              key={idx}
                              className="text-foreground flex cursor-pointer items-center space-x-2 text-sm"
                            >
                              <input
                                type="checkbox"
                                name={option.value}
                                data-id={config.id}
                                checked={isChecked}
                                onChange={(e) =>
                                  handleCheckBoxChange(e, false, currentValue)
                                }
                                className="border-border bg-card text-primary focus:ring-ring h-4 w-4 rounded focus:ring-2"
                              />
                              <span>{option.text}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {formState.changes.length > 0 && (
        <div className="mt-6 flex justify-end">
          <Button
            emphasis="solid"
            intent="brand"
            size="touch"
            onClick={handleSubmit}
            disabled={updateConfigMutation.isPending}
            isLoading={updateConfigMutation.isPending}
            className=""
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default DriverCompanySettings;
